import { useEffect, useRef, useState } from 'react';
import { ApiError, getMe, saveSettings, type CallerIdInfo, type ForwardInfo, type ForwardMode, type User } from '../api';
import { NumberNamesEditor } from './NumberNamesEditor';
import { alertFixSteps, browserOf, keepActiveTip } from '../phone/browser-help';
import { alertStatus, type AlertPermission } from '../phone/alerts';
import { voicemailNote } from '../phone/forward-text';
import { ChangePassword } from './ChangePassword';
import { Devices } from './Devices';
import { ForwardSetting } from './ForwardSetting';
import { TwoStepSetting } from './TwoStepSetting';
import { TimeZoneSetting } from './TimeZoneSetting';
import { PlanSetting } from './PlanSetting';

const BROWSER = browserOf(navigator.userAgent);
const SITE = window.location.host;

/**
 * The user's own settings, in a modal dialog: Do Not Disturb, "Ring all my
 * devices", forwarding (all devices); call alerts and the relay switch (this
 * browser only).
 */
export function Settings({
  user,
  callerId,
  onNumberName,
  dnd,
  onDnd,
  forward,
  onForward,
  alerts,
  onEnableAlerts,
  relayAlways,
  relayAvailable,
  relayOk,
  onRelay,
  onClose,
  onSaved,
  usageKey = 0,
}: {
  user: User;
  /** the user's numbers and their names (Numbers PR 2) */
  callerId?: CallerIdInfo | null;
  onNumberName?: (number: string, nickname: string | null) => Promise<string | null>;
  dnd: boolean;
  onDnd: (on: boolean) => Promise<string | null>;
  forward: ForwardInfo | null;
  onForward: (change: { mode: ForwardMode; number?: string | null }) => Promise<string | null>;
  alerts: AlertPermission;
  onEnableAlerts: () => Promise<void>;
  relayAlways: boolean;
  relayAvailable: boolean;
  relayOk: boolean | null;
  onRelay: (on: boolean) => void;
  onClose: () => void;
  onSaved: (u: User) => void;
  /** PR 2: changes when the plan minutes may have changed (live event) */
  usageKey?: number;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [ringAll, setRingAll] = useState(user.ringAll !== false);
  const [saving, setSaving] = useState<'ringAll' | 'dnd' | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [alertHelp, setAlertHelp] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  // read the account again on open: an admin may have changed it (e.g. the time zone)
  useEffect(() => {
    getMe().then(onSaved, () => undefined);
  }, []);

  const toggleRingAll = async () => {
    const next = !ringAll;
    setRingAll(next);
    setSaving('ringAll');
    setMessage(null);
    try {
      const { user: u } = await saveSettings({ ringAll: next });
      onSaved(u);
      setMessage({ tone: 'ok', text: 'Saved. It applies to the next incoming call.' });
    } catch (err) {
      setRingAll(!next);
      setMessage({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Couldn’t save. Try again.' });
    } finally {
      setSaving(null);
    }
  };

  const toggleDnd = async () => {
    setSaving('dnd');
    setMessage(null);
    const err = await onDnd(!dnd);
    setSaving(null);
    setMessage(err ? { tone: 'bad', text: err } : { tone: 'ok', text: 'Saved.' });
  };

  return (
    <dialog ref={ref} className="sheet" aria-labelledby="settings-title" onClose={onClose} onCancel={onClose}>
      <div className="sheet-head">
        <h2 id="settings-title">Settings</h2>
        <button className="btn btn-quiet btn-small" type="button" onClick={() => ref.current?.close()}>
          Done
        </button>
      </div>

      <div className="setting">
        <div className="setting-text">
          <span className="setting-name" id="dnd-label">
            Do Not Disturb
          </span>
          <span className="setting-help" id="dnd-help">
            {dnd ? 'Incoming calls don’t ring. Callers go straight to voicemail.' : 'Incoming calls ring as usual.'}
          </span>
        </div>
        <button
          type="button"
          role="switch"
          className="switch"
          aria-checked={dnd}
          aria-labelledby="dnd-label"
          aria-describedby="dnd-help"
          disabled={saving !== null}
          onClick={() => void toggleDnd()}
        >
          <span className="switch-knob" aria-hidden />
        </button>
      </div>

      <div className="setting">
        <div className="setting-text">
          <span className="setting-name" id="ring-all-label">
            Ring all my devices
          </span>
          <span className="setting-help" id="ring-all-help">
            {ringAll
              ? 'Incoming calls ring on every device where you’re signed in. Answer on one and the others stop; decline on one and the caller goes to voicemail.'
              : 'Incoming calls ring on one of your devices only.'}
          </span>
        </div>
        <button
          type="button"
          role="switch"
          className="switch"
          aria-checked={ringAll}
          aria-labelledby="ring-all-label"
          aria-describedby="ring-all-help"
          disabled={saving !== null}
          onClick={() => void toggleRingAll()}
        >
          <span className="switch-knob" aria-hidden />
        </button>
      </div>

      <ForwardSetting forward={forward} disabled={saving !== null} onSave={onForward} />

      <p className="setting-note">{voicemailNote(forward)}</p>

      {callerId && callerId.numbers.length > 0 && onNumberName && (
        <>
          <h3 className="setting-group">Your numbers</h3>
          <div className="setting setting-stack">
            <span className="setting-help">
              Give your numbers a name, like Sales or Personal. Calls, history and Calling from show it next to the number.
            </span>
            <NumberNamesEditor numbers={callerId.numbers} names={callerId.names} onSave={onNumberName} idPrefix="my-nn" />
          </div>
        </>
      )}

      <PlanSetting reloadKey={usageKey} />

      <h3 className="setting-group">Account</h3>
      <TimeZoneSetting user={user} onSaved={onSaved} />
      <ChangePassword email={user.email} />
      <TwoStepSetting email={user.email} />
      <Devices />

      <h3 className="setting-group">On this browser</h3>
      <div className="setting">
        <div className="setting-text">
          <span className="setting-name">Call alerts</span>
          <span className="setting-help">{alertStatus(alerts)}</span>
          {alerts === 'denied' && alertHelp && (
            <ol className="setting-steps">
              {alertFixSteps(BROWSER, SITE).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </div>
        {alerts === 'default' && (
          <button className="btn btn-small" type="button" onClick={() => void onEnableAlerts()}>
            Turn on
          </button>
        )}
        {alerts === 'denied' && (
          <button className="btn btn-small" type="button" aria-expanded={alertHelp} onClick={() => setAlertHelp(!alertHelp)}>
            {alertHelp ? 'Hide steps' : 'How to fix'}
          </button>
        )}
      </div>
      <p className="setting-note">
        Calls ring while CloudPhone is open in a tab, even in the background.{' '}
        {alerts === 'granted' ? 'Not seeing alerts? Check that notifications for this browser are on in your computer’s settings and that Do Not Disturb / Focus is off. ' : ''}
        {keepActiveTip(BROWSER, SITE) ?? ''}
      </p>

      <details className="setting-advanced">
        <summary>Advanced</summary>
        <div className="setting">
          <div className="setting-text">
            <span className="setting-name" id="relay-label">
              Always connect calls through the CloudPhone relay
            </span>
            <span className="setting-help" id="relay-help">
              {relayAvailable
                ? 'Turn on if calls on this network have no audio, cut out, or drop (some offices, hotels and VPNs block the direct path). Audio may have a little more delay. This browser only; applies to the next call.'
                : 'Not available: no relay server is set up for your account.'}
            </span>
            {relayAvailable && relayAlways && relayOk === false && (
              <span className="setting-help setting-warn" role="status">
                The relay can’t be reached from this network right now, so calls use the normal path.
              </span>
            )}
          </div>
          <button
            type="button"
            role="switch"
            className="switch"
            aria-checked={relayAlways && relayAvailable}
            aria-labelledby="relay-label"
            aria-describedby="relay-help"
            disabled={!relayAvailable}
            onClick={() => onRelay(!relayAlways)}
          >
            <span className="switch-knob" aria-hidden />
          </button>
        </div>
      </details>

      {message && (
        <p className={`setting-msg setting-msg-${message.tone}`} role={message.tone === 'bad' ? 'alert' : 'status'}>
          {message.text}
        </p>
      )}
    </dialog>
  );
}
